// @ts-nocheck
import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface DiscoverAthlete {
  id: string;
  firstName: string | null;
  lastName: string | null;
  username: string | null;
  avatarUrl: string | null;
  isFollowed: boolean;
}

export function useDiscoverAthletes(currentUserId?: string) {
  const [followedIds, setFollowedIds] = useState<string[]>([]);

  // Fetch followed IDs
  useEffect(() => {
    if (!currentUserId) {
      setFollowedIds([]);
      return;
    }

    const fetchFollowedIds = async () => {
      const { data } = await supabase
        .from('profile_follows')
        .select('followed_id')
        .eq('follower_id', currentUserId);
      
      setFollowedIds(data?.map(f => f.followed_id) || []);
    };

    fetchFollowedIds();
  }, [currentUserId]);

  // Popular athletes - those with most followers
  const { data: popularAthletes = [], isLoading: isLoadingPopular } = useQuery({
    queryKey: ['popular-athletes', followedIds],
    queryFn: async () => {
      // Get athletes with follower counts
      const { data: followCounts, error: countError } = await supabase
        .from('profile_follows')
        .select('followed_id')
        .limit(500);

      if (countError) throw countError;

      // Count followers per profile
      const followerCounts: Record<string, number> = {};
      followCounts?.forEach(f => {
        followerCounts[f.followed_id] = (followerCounts[f.followed_id] || 0) + 1;
      });

      // Get top profile IDs by follower count
      const topProfileIds = Object.entries(followerCounts)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 20)
        .map(([id]) => id);

      if (topProfileIds.length === 0) {
        // Fallback: just get some athletes
        const { data: profiles } = await supabase
          .from('profiles')
          .select('id, name, username, avatar_url')
          .eq('role', 'athlete')
          .limit(20);

        return (profiles || []).map(p => ({
          id: p.id,
          firstName: p.name?.split(' ')[0] || null,
          lastName: p.name?.split(' ').slice(1).join(' ') || null,
          username: p.username,
          avatarUrl: p.avatar_url,
          isFollowed: followedIds.includes(p.id),
        }));
      }

      // Fetch profile details for top profiles
      const { data: profiles, error: profileError } = await supabase
        .from('profiles')
        .select('id, name, username, avatar_url')
        .in('id', topProfileIds);

      if (profileError) throw profileError;

      // Sort by follower count
      const sorted = (profiles || []).sort((a, b) => 
        (followerCounts[b.id] || 0) - (followerCounts[a.id] || 0)
      );

      return sorted.map(p => ({
        id: p.id,
        firstName: p.name?.split(' ')[0] || null,
        lastName: p.name?.split(' ').slice(1).join(' ') || null,
        username: p.username,
        avatarUrl: p.avatar_url,
        isFollowed: followedIds.includes(p.id),
      })) as DiscoverAthlete[];
    },
    staleTime: 1000 * 60 * 5,
  });

  return {
    popularAthletes,
    isLoadingPopular,
  };
}
